import {PaginatedChatRepository} from "@web-libs/features/chat/api/chat.repository";
import {HttpClient} from "@web-libs/core/http";
import {LocalFileCache} from "@web-libs/features/chat/api/local-file-cache/local-file-cache";
import {
    ChatroomSimpleDTO,
    parseChatroomSimpleDTO,
    parseRoomSimpleDTO,
    RoomHistoryDTO,
    RoomSimpleDTO
} from "@web-libs/features/chat/api/dto/dto.types";
import {Page} from "@web-libs/core/pagination/api/pagination.functions";
import {TrmzFileDTOConverter} from "@web-libs/features/files/trmz-file/api/trmz-file-dto-converter";
import {
    encodeMediaMessage,
    encodeTextMessage,
    parseChatMessage
} from "@web-libs/features/chat/api/dto/chat-message.parser";
import {ChatroomUpdate} from "@web-libs/features/chat/domain/chatroom.functions";
import GroupPlaceholder from "@web-public/group-placeholder.png";
import {Chatroom, NewChatroom} from "@web-libs/features/chat/domain/types/chatroom.type";
import {
    ChatMessage,
    SendFileMessage,
    SendMediumMessage,
    SendTextMessage,
    SendVoiceMessage
} from "@web-libs/features/chat/domain/types/chat-message.types";
import {Chat} from "@web-libs/features/chat/domain/types/chat.type";
import {TrmzFileRepository} from "@web-libs/features/files/trmz-file/domain/trmz-file.repository";
import {ChatSocket} from "@web-libs/features/chat/api/chat.socket";
import {TrmzFileDTO} from "@web-libs/features/files/trmz-file/api/trmz-file.types";
import {MessageDTO} from "@web-libs/features/chat/api/dto/chat-message-dto.types";

type CustomStatus<T> = {
    status: string;
    object: T;
}

export const createRemotePaginatedChatRepository = (httpClient: HttpClient, trmzFileRepo: TrmzFileRepository, converter: TrmzFileDTOConverter, chatSocket: ChatSocket): PaginatedChatRepository => {

    const defaultRoomPage = 999;
    const defaultMessagePage = 20;
    const localFileCache = new LocalFileCache();

    let _ownUserId: string | undefined = undefined;
    const messageCallbacks: ((message: ChatMessage, chatroomId: string) => void)[] = [];

    const translateMessage = (message: ChatMessage) => {
        if(message.type === "text") {
            return httpClient.get(`/chat/messages/${message.messageId}/translated`).then((response: any) => {
                const decoded = JSON.parse(response.message);
                message.text = decoded.msg;
                return message;
            });
        }
        return message;
    }

    chatSocket.onMessage(async (message, chatroomId) => {
        const ownUserId = await getUserId();
        const messageDTO = JSON.parse(message) as MessageDTO; // Unsure if thats really it.
        let parsedMessage = parseChatMessage(messageDTO, ownUserId, converter, localFileCache);
        if(!parsedMessage.ownMessage) {
            parsedMessage = await translateMessage(parsedMessage);
        }
        messageCallbacks.forEach(callback => callback(parsedMessage, chatroomId));
    })

    const getUserId = async (): Promise<string> => {
        if (_ownUserId) return _ownUserId;
        return httpClient.get("/users/self").then((response: any) => {
            _ownUserId = response.id;
            return response.id;
        })
    }

    const getChat = async (): Promise<Chat> => {
        const ownUserId = await getUserId();
        const response = await httpClient.get<Page<ChatroomSimpleDTO>>(`/chat/rooms`, {queryParams: {size: defaultRoomPage}});
        const chatrooms = response.content.map(chatroomSimpleDTO => parseChatroomSimpleDTO(chatroomSimpleDTO, ownUserId!, converter, (messageContent) => {
            const userList = chatroomSimpleDTO.chatRoomUsers
            const senderId = JSON.parse(messageContent).senderId;
            const senderInList = userList.find(user => user.user.id === senderId)?.user
            const senderNameInList = senderInList ? (senderInList.firstName && senderInList.lastName ? `${senderInList.firstName} ${senderInList.lastName}` : senderInList.username) : undefined;
            return parseChatMessage({
                message: messageContent,
                senderId: senderId,
                senderName: senderNameInList
            }, ownUserId!, converter, localFileCache);
        }));
        return {
            chatrooms
        }
    }

    const getChatroom = async (id: string): Promise<ChatroomUpdate> => {
        const ownUserId = await getUserId();
        const _detailsResponse = await httpClient.get<RoomSimpleDTO>(`/chat/rooms/${id}`);
        const chatroomUpdate = parseRoomSimpleDTO(_detailsResponse, ownUserId, converter);
        const _messagesResponse = await getChatHistoryPage(id, 0)
        const messages = _messagesResponse.content.map(message => parseChatMessage(message, ownUserId!, converter, localFileCache));
        return {
            ...chatroomUpdate,
            messages
        }
    }

    const sendMessage = async (chatroomId: string, message: SendTextMessage): Promise<void> => {
        const ownUserId = await getUserId();
        const encodedMessage = encodeTextMessage(message, ownUserId);
        chatSocket.sendMessage(encodedMessage, chatroomId);
    }

    const sendFile = async (chatroomId: string, message: SendFileMessage): Promise<void> => {
        const ownUserId = await getUserId();
        const uploadedFileId = await _uploadChatMedium(message.file, chatroomId);
        const encodedMessage = encodeMediaMessage(
            message,
            ownUserId,
            uploadedFileId,
            chatroomId
        )
        chatSocket.sendMessage(encodedMessage, chatroomId);
    }

    const sendMedium = async (chatroomId: string, message: SendMediumMessage): Promise<void> => {
        const ownUserId = await getUserId();
        const uploadedFileId = await _uploadChatMedium(message.medium, chatroomId);
        const encodedMessage = encodeMediaMessage(
            message,
            ownUserId,
            uploadedFileId,
            chatroomId
        )
        chatSocket.sendMessage(encodedMessage, chatroomId);
    }


    const sendVoice = async (chatroomId: string, message: SendVoiceMessage): Promise<void> => {
        const ownUserId = await getUserId();
        const uploadedFileId = await _uploadChatMedium(new File([message.blob], "voice.mp4", {type: 'audio/mp4'}), chatroomId);
        const encodedMessage = encodeMediaMessage(
            message,
            ownUserId,
            uploadedFileId,
            chatroomId
        )
        chatSocket.sendMessage(encodedMessage, chatroomId);
    }

    const onMessage = (callback: (message: ChatMessage, chatroomId: string) => void): () => void => {
        messageCallbacks.push(callback);
        return () => {
            const index = messageCallbacks.indexOf(callback);
            if (index > -1) {
                messageCallbacks.splice(index, 1);
            }
        }
    }

    const _uploadChatMedium = async (medium: File, chatroomId: string): Promise<string> => {
        const formData = new FormData();
        formData.append("file", medium);
        const response = await httpClient.postFormData<CustomStatus<TrmzFileDTO>>(`/rooms/${chatroomId}/chatMedia/upload`, formData);
        return response.object.id;
    }

    const createChatroom = async (newChatroom: NewChatroom): Promise<Chatroom> => {
        const ownUserId = await getUserId();
        const response = newChatroom.isSingleChat ? await _createSingleChatroom(newChatroom) : await _createGroupChatroom(newChatroom);
        const update = parseRoomSimpleDTO(response.object, ownUserId, converter);
        return {
            ...update,
            name: update.name ?? "#FixMe",
            type: update.type ?? (newChatroom.isSingleChat ? "single" : "group"),
            creationDate: update.creationDate ?? new Date(),
            messages: [],
            users: update.users ?? [],
            unreadMessages: 0,
            imageUrl: newChatroom.image ? URL.createObjectURL(newChatroom.image) : update.imageUrl,
            initialSortDate: update.creationDate ?? new Date(),
            oneToOne: newChatroom.isSingleChat,
            firstOneToOneUser: newChatroom.isSingleChat ? update.users?.[0] : undefined,
            secondOneToOneUser: newChatroom.isSingleChat ? update.users?.[1] : undefined,
        };
    }

    const _createGroupChatroom = async (newChatroom: NewChatroom): Promise<CustomStatus<RoomSimpleDTO>> => {
        const response = await httpClient.post<CustomStatus<RoomSimpleDTO>>(`/chat/rooms/manyToMany`, {
            userIds: newChatroom.users,
            groupName: newChatroom.name
        });

        if (newChatroom.image) {
            const chatroomId = response.object.id
            const uploadedFile = await trmzFileRepo.uploadChatroomImage(newChatroom.image);
            await httpClient.put(`/chat/rooms/${chatroomId}/logo`, uploadedFile.id, {
                queryParams: {
                    fileId: uploadedFile.id
                }
            });
        }

        return response;
    }

    const _createSingleChatroom = async (newChatroom: NewChatroom): Promise<CustomStatus<RoomSimpleDTO>> => {
        return httpClient.post<CustomStatus<RoomSimpleDTO>>(`/chat/rooms/oneToOne`, {
            userId: newChatroom.users[0]
        });
    }

    const loadChatroomMessages = async (chatroomId: string, pageNumber: number): Promise<Page<ChatMessage>> => {
        const ownUserId = await getUserId();
        const _messagesResponse = await getChatHistoryPage(chatroomId, pageNumber);
        const messages = _messagesResponse.content.map(message => parseChatMessage(message, ownUserId!, converter, localFileCache));
        return {
            ..._messagesResponse,
            content: messages
        }
    }

    const getChatHistoryPage = async (chatroomId: string, pageNumber: number): Promise<Page<RoomHistoryDTO>> => {
        const response = await httpClient.get<Page<RoomHistoryDTO>>(`/chat/rooms/${chatroomId}/chatHistory`, {queryParams: {size: defaultMessagePage, page: pageNumber, sort: "insertDate,desc"}});
        return {
            ...response,
            content: response.content.reverse()
        }
    }

    const readChatroom = async (id: string): Promise<void> => {
        return httpClient.post(`/chat/rooms/${id}/setRead`, {})
    }

    const startInstitutionChat = async (instId:string): Promise<Chatroom> => {
        const createdChatroom = await httpClient.post<CustomStatus<RoomSimpleDTO>>(`/chat/rooms/startInstitutionChat/${instId}`, {})
        const ownUserId = await getUserId();
        const update = parseRoomSimpleDTO(createdChatroom.object, ownUserId, converter);
        return {
            ...update,
            name: update.name ?? "#FixMe",
            type: update.type ?? "group",
            creationDate: update.creationDate ?? new Date(),
            messages: [],
            users: update.users ?? [],
            unreadMessages: 0,
            initialSortDate: update.creationDate ?? new Date(),
            oneToOne: false,
        };
    }

    const deleteChatMessage = async (chatroomId: string, messageId: string): Promise<void> => {
        return httpClient.delete(`/chat/rooms/${chatroomId}/me/deleteMessages`, {queryParams: {messageIds: [messageId]}})
    }

    return {
        getChat,
        getChatroom,
        readChatroom,
        sendMessage,
        sendFile,
        sendMedium,
        sendVoice,
        onMessage,
        createChatroom,
        loadChatroomMessages,
        startInstitutionChat,
        deleteMessage: deleteChatMessage
    }
}
